import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { be1Fetch } from '../../../../config/api';
import { useEscapeKey } from '../../../../hooks/useEscapeKey';
import { showAlert } from '../../../common/AppDialog';
import NodeDataModal, { NodeDataTarget } from './NodeDataModal';
import { useTranslation } from 'react-i18next';
import { errorText } from '../../../../services/errorText';
import { withTimeZone } from '../../../../utils/timeZone';

interface Snapshot { at: Date; limit: number; columns: string[]; rows: any[]; warning?: string | null }

interface Props {
  /** 캔버스의 데이터 노드 (csvData / rdbData) */
  node: any;
  onClose: () => void;
}

const LIMITS = [50, 100, 300];

/** 노드별 직전 조회 — 실행 전에 한 번 열어 보고 실행 후 다시 열면 '직전 조회' 탭으로 견줘 볼 수 있다 (새로고침하면 사라짐) */
const lastRead = new Map<string, Snapshot>();

const timeOf = (d: Date) => d.toTimeString().slice(0, 8); // HH:MM:SS

/**
 * 데이터 노드 값 보기 — 캔버스 노드의 데이터 보기 아이콘에서 연다.
 * 파일/테이블을 지금 읽으므로 실행 전에 열면 실행 전 값, 실행 후에 열면 실행 후 값이다.
 */
const DataNodeDataModal: React.FC<Props> = ({ node, onClose }) => {
  const { t } = useTranslation();
  useEscapeKey(onClose);
  const cfg = node.data?.config || {};
  const isCsv = node.type === 'csvData';
  const source = isCsv ? (cfg.filepath || '') : [cfg.schema, cfg.table].filter(Boolean).join('.');

  const [limit, setLimit] = useState(100);
  const [cur, setCur] = useState<Snapshot | null>(null);
  const [prev, setPrev] = useState<Snapshot | null>(null);
  const [tab, setTab] = useState<'cur' | 'prev'>('cur');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 가장 최근에 읽은 값 — '다시 조회' 하면 이것이 직전 조회로 밀려난다
  const latest = useRef<Snapshot | null>(lastRead.get(node.id) || null);

  /** shift=true 면 지금 보이는 값을 직전 조회로 넘긴다 (조회 건수만 바꿀 때는 넘기지 않는다) */
  const load = useCallback(async (shift: boolean) => {
    if (isCsv ? !cfg.filepath : !(cfg.connection && cfg.table)) {
      setError(isCsv ? t('property:dv.noPath') : t('property:dv.noTable'));
      return;
    }
    setLoading(true); setError(null); setTab('cur');
    try {
      const body = isCsv
        ? { path: cfg.filepath, encoding: cfg.encoding || 'UTF-8', format: cfg.format || 'delimited', delimiter: cfg.delimiter || ',',
            hasHeader: cfg.hasHeader !== false, fieldWidths: cfg.fieldWidths || null, fieldNames: cfg.fieldNames || null, limit }
        : { kind: 'db', connection: cfg.connection, schema: cfg.schema || undefined, table: cfg.table, limit };
      const r = await be1Fetch(isCsv ? '/api/fs/sam-preview' : '/api/data/preview', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(errorText(j, `HTTP ${r.status}`));
      const columns: string[] = j.columns || [];
      // SAM 미리보기는 행을 배열로 준다 — 항목명으로 묶어 DB 와 같은 모양으로
      const rows = (j.rows || []).map((row: any) => (Array.isArray(row) ? Object.fromEntries(columns.map((c, i) => [c, row[i]])) : row));
      const snap: Snapshot = { at: new Date(), limit, columns, rows, warning: j.warning };
      if (shift && latest.current) setPrev(latest.current);
      latest.current = snap;
      lastRead.set(node.id, snap);
      setCur(snap);
    } catch (e) {
      setCur(null);
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node.id, isCsv, cfg.filepath, cfg.connection, cfg.schema, cfg.table, limit]);

  // 처음 열 때는 지난번에 열었을 때의 값을 직전 조회로 둔다
  const opened = useRef(false);
  useEffect(() => { load(!opened.current); opened.current = true; }, [load]);

  const shown = tab === 'prev' ? prev : cur;
  const tabCls = (on: boolean) => `px-3 py-1 text-xs rounded ${on ? 'bg-blue-600 text-white' : 'border border-gray-300 bg-white hover:bg-gray-100 text-gray-700'}`;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-40" onMouseDown={onClose}>
      <div className="bg-white rounded-lg shadow-2xl w-[980px] max-w-[95vw] h-[78vh] flex flex-col" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-gray-800">{t('property:dv.title', { name: node.data?.label || node.id })}</div>
            <div className="text-xs text-gray-500 truncate">{isCsv ? t('property:dv.kindCsv') : t('property:dv.kindDb')} · {source || t('property:dv.unset')}</div>
          </div>
          <button className="px-2 py-1 text-xs rounded hover:bg-gray-100 text-gray-600" onClick={onClose} title={t('property:dv.closeEsc')}>✕</button>
        </div>

        <div className="flex items-center gap-2 px-4 py-2 border-b border-gray-200">
          <button className={tabCls(tab === 'cur')} onClick={() => setTab('cur')}>
            {t('property:dv.now')} {cur ? `(${timeOf(cur.at)})` : ''}
          </button>
          <button className={`${tabCls(tab === 'prev')} disabled:opacity-40`} disabled={!prev} onClick={() => setTab('prev')}
            title={prev ? '' : t('property:dv.prevTitle')}>
            {t('property:dv.prev')} {prev ? `(${timeOf(prev.at)})` : ''}
          </button>
          <label className="ml-auto flex items-center gap-1 text-xs text-gray-600">{t('property:dv.limit')}
            <select className="border border-gray-300 rounded px-1.5 py-0.5 text-xs bg-white" value={limit} onChange={(e) => setLimit(Number(e.target.value))}>
              {LIMITS.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <button className="px-2 py-0.5 text-xs rounded border border-gray-300 bg-white hover:bg-gray-100 text-gray-700 disabled:opacity-50"
            disabled={loading} onClick={() => load(true)} title={t('property:dv.reloadTitle')}>{t('property:dv.reload')}</button>
        </div>

        <div className="flex-1 min-h-0 overflow-auto p-3">
          {tab === 'cur' && loading ? <div className="text-xs text-gray-500">{t('property:dv.reading')}</div>
            : tab === 'cur' && error ? <div className="text-xs text-red-500">{error}</div>
              : !shown ? null
                : (
                  <>
                    <div className="text-xs text-gray-500 mb-1">
                      {t('property:dv.readAt', { time: timeOf(shown.at), n: shown.rows.length })}{shown.rows.length >= shown.limit ? t('property:dv.topOnly', { n: shown.limit }) : ''}
                      {tab === 'cur' && prev && t('property:dv.prevCount', { n: prev.rows.length })}
                    </div>
                    {shown.warning && <div className="text-xs text-amber-600 mb-1">{shown.warning}</div>}
                    {shown.rows.length === 0 ? <div className="text-xs text-gray-500">{t('property:dv.noData')}</div> : (
                      <div className="overflow-auto border border-gray-200 rounded">
                        <table className="text-xs w-full">
                          <thead className="bg-gray-50 sticky top-0">
                            <tr>
                              <th className="px-2 py-1 text-right text-gray-400 font-normal border-b border-gray-200">#</th>
                              {shown.columns.map((c) => <th key={c} className="px-2 py-1 text-left font-medium text-gray-700 border-b border-gray-200 whitespace-nowrap">{c}</th>)}
                            </tr>
                          </thead>
                          <tbody>
                            {shown.rows.map((r, i) => (
                              <tr key={i} className="hover:bg-gray-50">
                                <td className="px-2 py-0.5 text-right text-gray-300 border-b border-gray-100 select-none">{i + 1}</td>
                                {shown.columns.map((c) => (
                                  <td key={c} className="px-2 py-0.5 border-b border-gray-100 whitespace-nowrap max-w-[280px] truncate" title={String(r[c] ?? '')}>
                                    {r[c] === null || r[c] === undefined ? <span className="text-gray-300">(null)</span> : String(r[c])}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}
        </div>

        <div className="px-4 py-2.5 border-t border-gray-200 flex items-center gap-2">
          <span className="text-xs text-gray-500 mr-auto">
            {t('property:dv.footer')}
          </span>
          <button className="px-3 py-1 text-xs rounded border border-gray-300 bg-white hover:bg-gray-100 text-gray-700" onClick={onClose}>{t('property:dv.close')}</button>
        </div>
      </div>
    </div>
  );
};

/**
 * 캔버스 노드의 데이터 보기 아이콘(openflow:open-node-data)을 받아 모달을 띄운다 — 레이아웃에 하나만 둔다.
 * SAM파일/DB테이블은 지금 값을 읽고, 내부데이터는 파일/테이블이 아니므로 최근 실행에서 넘겨받은 값을 보여 준다.
 */
export const DataNodeViewHost: React.FC = () => {
  const { t } = useTranslation();
  const currentId = useSelector((s: any) => s.workflow.current?.id);
  const currentName = useSelector((s: any) => s.workflow.current?.name);
  const nodes = useSelector((s: any) => s.workflow.nodes);
  const edges = useSelector((s: any) => s.workflow.edges);
  const settings = useSelector((s: any) => s.workflow.settings);
  const [fileNode, setFileNode] = useState<any | null>(null);
  const [inner, setInner] = useState<{ dagId: string; node: NodeDataTarget; inputs: NodeDataTarget[] } | null>(null);

  useEffect(() => {
    const h = async (e: Event) => {
      const nodeId = (e as CustomEvent).detail?.nodeId;
      const node = (nodes || []).find((n: any) => n.id === nodeId);
      if (!node) return;
      if (node.type !== 'innerData') { setFileNode(node); return; }
      if (!currentId) { showAlert(t('property:dv.selectWorkflow')); return; }
      // 노드→태스크 매핑은 캔버스를 변환해야 알 수 있다 (속성 패널의 입력·출력 데이터와 같은 방식)
      try {
        const res = await be1Fetch('/api/canvas/preview', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ workflow_id: currentId, name: currentName, nodes, edges, schedule_interval: settings?.schedule, settings: withTimeZone(settings) }),
        });
        const body = await res.json();
        if (!res.ok) throw new Error(errorText(body, t('property:dv.convertFailed')));
        const taskMap: Record<string, string> = body.task_map || {};
        const labelOf = (id: string) => (nodes || []).find((n: any) => n.id === id)?.data?.label || id;
        setInner({
          dagId: body.dag_id,
          node: { nodeId, label: labelOf(nodeId), taskId: taskMap[nodeId] || '' },
          inputs: (edges || []).filter((ed: any) => ed.target === nodeId).map((ed: any) => ({
            nodeId: ed.source, label: labelOf(ed.source), taskId: taskMap[ed.source] || '',
            key: ed.sourceHandle === 'reject-path' ? 'rejects' : undefined,
          })),
        });
      } catch (err) {
        showAlert(t('property:dv.openFailed', { message: (err as Error).message }));
      }
    };
    window.addEventListener('openflow:open-node-data', h);
    return () => window.removeEventListener('openflow:open-node-data', h);
  }, [currentId, currentName, nodes, edges, settings, t]);

  return (
    <>
      {fileNode && <DataNodeDataModal node={fileNode} onClose={() => setFileNode(null)} />}
      {inner && <NodeDataModal dagId={inner.dagId} node={inner.node} inputs={inner.inputs} onClose={() => setInner(null)} />}
    </>
  );
};

export default DataNodeDataModal;
